/*
 * Copyright (c) 2021-2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: App.tsx
 * Project: mle-client
 * Committed last: 2021/11/11 @ 1732
 * ---
 */

import React, {useEffect, useState} from 'react';
import {FocusStyleManager, HotkeysTarget2, IHotkeyProps} from '@blueprintjs/core';
import {CombosNames, MleCombos} from '../common/mle-combos';
import classNames from 'classnames';
import {currentThemeIsDark, getOS, themeStorageKey} from '../common/utils';
import {Provider} from 'react-redux';
import {Dashboard} from '../pages/Dashboard';
import {Login} from '../pages/Login';
import {AuthPaths} from '../paths/auth.paths';
import {RootPaths} from '../paths/root.paths';
import {LabelPaths} from '../paths/label.paths';
import {OSs, Themes} from '../common/enums';
import {MleToaster} from '../common/toast';
import {store} from '../store';
import {LabelNotFound} from '../pages/LabelNotFound';
import {EditorProvider} from './editor/main/EditorProvider';
import {Route, Routes} from 'react-router-dom';
import {Navigate, useLocation} from 'react-router';
import {RootLoader} from './common/wrapper/RootLoader';
import {Authenticated} from './common/wrapper/Authenticated';
import {ResetPassword} from '../pages/ResetPassword';
import {PasswordForgot} from '../pages/PasswordForgot';
import {StandaloneFeaturesManager} from "../store/features/common/StandaloneFeature";
import {MyLabelAuthStandalone} from "../store/features/mylabel/auth/MLAuthStandalone";
import {ImportFeature} from "../store/features/mpes-io/ImportFeature";

/**
 * Global standalone features manager
 */
export const StandaloneFeatures = new StandaloneFeaturesManager([
  MyLabelAuthStandalone,
  ImportFeature,
]);

export const App = () => {
  const [currentTheme, setCurrentTheme] = useState(currentThemeIsDark() ? Themes.DARK : Themes.LIGHT);
  const location = useLocation();

  useEffect(() => {
    FocusStyleManager.onlyShowFocusOnTabs();
    StandaloneFeatures.initAll();
  });

  const applyCurrentTheme = (theme: Themes) => {
    localStorage.setItem(themeStorageKey, theme);
    setCurrentTheme(theme);
  };

  const switchTheme = () => {
    if (currentThemeIsDark()) {
      applyCurrentTheme(Themes.LIGHT);
      return MleToaster.getInstance().show({
        icon: 'flash',
        message: 'Tema chiaro'
      });
    }
    applyCurrentTheme(Themes.DARK);
    MleToaster.getInstance().show({
      icon: 'moon',
      message: 'Tema scuro'
    });
  };

  const hotkeys: IHotkeyProps[] = [
    {
      combo: MleCombos[CombosNames.SWITCH_THEME][getOS() ?? OSs.WINDOWS],
      global: true,
      label: CombosNames.SWITCH_THEME,
      onKeyDown: switchTheme
    },
  ];

  return (
    <Provider store={ store }>
      <HotkeysTarget2 hotkeys={ hotkeys }>
        <div
          className={ classNames('min-h-screen bp4-fill overflow-hidden',
            {
              'bp4-dark bg-bpdark-200': currentThemeIsDark(),
              'bg-bplight-100': !currentThemeIsDark()
            }) }>
          <RootLoader>
            <Routes>
              <Route
                path={ RootPaths.ROOT }
                element={ <Authenticated> <Navigate to={ RootPaths.DASHBOARD }/>
                </Authenticated> }/>
              <Route path={ AuthPaths.LOGIN } element={ <Login/> }/>
              <Route path={ RootPaths.DASHBOARD }
                     element={ <Authenticated><Dashboard/></Authenticated> }/>
              <Route path={ LabelPaths.BY_ID } element={
                <Authenticated><EditorProvider/></Authenticated> }/>
              <Route path={ LabelPaths.NOT_FOUND }
                     element={ <LabelNotFound/> }/>
              <Route path={ AuthPaths.RESET_PASSWORD }
                     element={ <ResetPassword/> }/>
              <Route path={ RootPaths.RESET_PASSWORD_BY_TOKEN }
                     element={ <ResetPassword/> }/>
              <Route path={ RootPaths.FORGOT_PASSWORD }
                     element={ <PasswordForgot/> }/>
            </Routes>
          </RootLoader>
        </div>
      </HotkeysTarget2>
    </Provider>
  );
};
